import { gql, useMutation } from '@apollo/client';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from 'hephaestus/web';
import { Refresh } from 'hephaestus/webIcons';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const CREATE_INTEGRATION_FORM_PACK = gql`
  mutation createIntegrationFormPack($input: CreateIntegrationFormPackInput!) {
    createIntegrationFormPack(input: $input) {
      integrationFormPack {
        id
      }
    }
  }
`;

export const CreateIntegrationFormPack = ({
  onNext,
  onBack,
  workflowCompletion,
  direction,
}: {
  onNext: () => void;
  onBack: () => void;
  workflowCompletion: any;
  direction: 'FORWARD' | 'BACKWARD';
}) => {
  const { t } = useTranslation(['workflowCompletions', 'shared']);
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const onBackRef = useRef(onBack);
  const hasAttemptedRef = useRef(false);

  onBackRef.current = onBack;

  const [createIntegrationFormPack] = useMutation(
    CREATE_INTEGRATION_FORM_PACK,
    {
      onCompleted: async (d: any) => {
        setError(null);
        await setWorkflowData({
          ...workflowData,
          integrationFormPackId:
            d.createIntegrationFormPack.integrationFormPack.id,
        });
        await onNext();
      },
    },
  );
  const attemptCreateIntegrationFormPack = useCallback(async () => {
    if (!workflowCompletion.organization?.id || !workflowData.signerMapping) {
      return;
    }

    setIsLoading(true);
    setError(null);

    const signerMapping = workflowData.signerMapping
      .filter((s: any) => s.firstName && s.lastName && s.email)
      .map((s: any) => ({
        signerKey: s.signerKey,
        firstName: s.firstName,
        lastName: s.lastName,
        email: s.email,
        signingOrder: s.signingOrder,
      }));

    const variables = {
      input: {
        workflowCompletionId: workflowCompletion.id,
        userId: workflowCompletion.objectId,
        organizationId: workflowCompletion.organization.id,
        signerMapping,
      },
      skipErrorHandler: true,
    };

    try {
      await createIntegrationFormPack({ variables });
    } catch (err) {
      setError(err as Error);
      setIsLoading(false);
    }
  }, [workflowCompletion, workflowData, createIntegrationFormPack]);

  useEffect(() => {
    if (hasAttemptedRef.current) {
      return;
    }
    hasAttemptedRef.current = true;
    if (direction === 'BACKWARD') {
      onBackRef.current();
      return;
    }
    void attemptCreateIntegrationFormPack();
  }, [attemptCreateIntegrationFormPack, direction]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        minHeight: '300px',
      }}
    >
      <Typography variant='headingMedium'>
        {t('workflowCompletions:createIntegrationFormPack.title')}
      </Typography>
      <Typography variant='bodyMedium' sx={{ mb: 4, fontWeight: 500 }}>
        {t('workflowCompletions:createIntegrationFormPack.description')}
      </Typography>
      {error && (
        <Box
          sx={{
            mb: 2,
          }}
        >
          <Alert severity='error'>{error.message}</Alert>
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Button
              onClick={attemptCreateIntegrationFormPack}
              disabled={isLoading}
              variant='text'
              startIcon={<Refresh />}
            >
              {t('shared:retry')}
            </Button>
          </Box>
        </Box>
      )}
      {isLoading && <CircularProgress />}
    </Box>
  );
};
